import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import EditIcon from "@mui/icons-material/Edit";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import {
  fetchClassesForWeek,
  createClass,
  updateClass,
  setClassLeader,
  scheduleClassForWeek,
  descheduleClassFromWeek,
  fetchWeeklyStaffScheduling,
} from "../api/classesApi";
import CreateClassDialog from "./CreateClassDialog";
import EditClassDialog from "./EditClassDialog";
import AddExistingClassDialog from "./AddExistingClassDialog";
import CopyFromWeekDialog from "./CopyFromWeekDialog";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

function ManageClassesView({
  selectedCampus,
  selectedCampusName,
  selectedWeek,
  weekLabel,
  allWeeks = [],
  formatWeekLabel,
  showToast,
  onClassesChanged,
  onOpenStaffSchedule,
}) {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [addExistingOpen, setAddExistingOpen] = useState(false);
  const [copyFromWeekOpen, setCopyFromWeekOpen] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [saving, setSaving] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [teachers, setTeachers] = useState([]);
  const [teachersLoading, setTeachersLoading] = useState(false);

  const loadClasses = useCallback(async () => {
    if (!selectedWeek?.id) {
      setClasses([]);
      return;
    }
    setLoading(true);
    try {
      const data = await fetchClassesForWeek(selectedWeek.id);
      setClasses(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to fetch classes for week", err);
      setClasses([]);
      showToast?.("Failed to load classes", "error");
    } finally {
      setLoading(false);
    }
  }, [selectedWeek?.id, showToast]);

  useEffect(() => {
    loadClasses();
  }, [loadClasses]);

  const loadTeachers = useCallback(async () => {
    if (!selectedCampusName || !selectedWeek?.startDate) {
      setTeachers([]);
      return;
    }
    setTeachersLoading(true);
    try {
      const data = await fetchWeeklyStaffScheduling(
        selectedCampusName,
        selectedWeek.startDate
      );
      const rows = Array.isArray(data?.rows) ? data.rows : [];
      const mapped = rows
        .filter((r) => r.adultId && Array.isArray(r.scheduledDates) && r.scheduledDates.length > 0)
        .map((r) => {
          const [first = "", ...rest] = String(r.name || "").trim().split(/\s+/);
          return {
            adultId: r.adultId,
            adultName: first,
            adultSurname: rest.join(" "),
            campus: data?.campus || selectedCampusName,
          };
        });
      setTeachers(mapped);
    } catch (err) {
      console.error("Failed to fetch staff scheduling", err);
      setTeachers([]);
    } finally {
      setTeachersLoading(false);
    }
  }, [selectedCampusName, selectedWeek?.startDate]);

  useEffect(() => {
    loadTeachers();
  }, [loadTeachers]);

  const handleOpenCreate = () => {
    setCreateOpen(true);
  };

  const handleOpenEdit = (cls) => {
    setEditingClass(cls);
    setEditOpen(true);
  };

  const handleInlineTeacherChange = async (cls, adultId) => {
    if (!selectedWeek?.startDate || !selectedWeek?.endDate) return;
    try {
      await setClassLeader(
        cls.id,
        adultId || null,
        selectedWeek.startDate,
        selectedWeek.endDate
      );
      showToast?.("Teacher updated", "success");
      await loadClasses();
      onClassesChanged?.();
    } catch (err) {
      console.error("Failed to update teacher", err);
      showToast?.(err.message || "Failed to update teacher", "error");
    }
  };

  const handleEditSave = async (form) => {
    if (!editingClass) return;
    setSaving(true);
    try {
      await updateClass(editingClass.id, {
        groupName: form.groupName,
        campus: selectedCampusName,
        capacity: form.capacity,
        notes: form.notes,
        groupType: "CLASS",
      });

      const previousLeaderId = editingClass.leaderAdultId ?? null;
      const newLeaderId = form.leaderId ?? null;
      if (previousLeaderId !== newLeaderId) {
        await setClassLeader(
          editingClass.id,
          newLeaderId,
          selectedWeek.startDate,
          selectedWeek.endDate
        );
      }

      showToast?.("Class updated", "success");
      setEditOpen(false);
      setEditingClass(null);
      await loadClasses();
      onClassesChanged?.();
    } catch (err) {
      console.error("Failed to update class", err);
      showToast?.(err.message || "Failed to update class", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = async (form) => {
    if (!selectedWeek?.id) return;
    setSaving(true);
    try {
      await createClass({
        groupName: form.groupName,
        campus: selectedCampusName,
        capacity: form.capacity,
        notes: form.notes,
        groupType: "CLASS",
        calendarWeekId: selectedWeek.id,
        leaderId: form.leaderId,
      });
      showToast?.("Class created", "success");
      setCreateOpen(false);
      await loadClasses();
      onClassesChanged?.();
    } catch (err) {
      console.error("Failed to create class", err);
      showToast?.(err.message || "Failed to create class", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDeschedule = async (cls) => {
    if (!selectedWeek?.id) return;
    const confirmed = window.confirm(
      `Remove "${cls.groupName}" from week ${weekLabel}? The class will still exist for any other scheduled weeks.`
    );
    if (!confirmed) return;
    try {
      await descheduleClassFromWeek(cls.id, selectedWeek.id);
      showToast?.("Class removed from this week", "success");
      await loadClasses();
      onClassesChanged?.();
    } catch (err) {
      console.error("Failed to deschedule class", err);
      showToast?.(err.message || "Failed to remove class from week", "error");
    }
  };

  const handleScheduleExisting = async (cls) => {
    if (!selectedWeek?.id) return;
    setScheduling(true);
    try {
      await scheduleClassForWeek(cls.id, selectedWeek.id);
      showToast?.(`"${cls.groupName}" added to this week`, "success");
      await loadClasses();
      onClassesChanged?.();
    } catch (err) {
      console.error("Failed to schedule existing class", err);
      showToast?.(err.message || "Failed to add class to week", "error");
    } finally {
      setScheduling(false);
    }
  };

  const sortedClasses = useMemo(
    () =>
      [...classes].sort((a, b) =>
        String(a.groupName || "").localeCompare(String(b.groupName || ""))
      ),
    [classes]
  );

  if (!selectedCampus) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          Pick a campus to manage classes.
        </Typography>
      </Box>
    );
  }

  if (!selectedWeek) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          Pick a week to manage classes.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Typography variant="subtitle1">
          Classes for {selectedCampusName} week {weekLabel}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<ContentCopyIcon />}
            onClick={() => setCopyFromWeekOpen(true)}
          >
            Copy from another week
          </Button>
          <Button
            variant="outlined"
            startIcon={<PlaylistAddIcon />}
            onClick={() => setAddExistingOpen(true)}
          >
            Add existing class
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreate}
          >
            Create class
          </Button>
        </Stack>
      </Stack>

      {loading ? (
        <Box py={4} display="flex" justifyContent="center">
          <CircularProgress size={28} />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Class name</TableCell>
                <TableCell>Capacity</TableCell>
                <TableCell>Teacher</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedClasses.map((cls) => (
                <TableRow key={cls.id} hover>
                  <TableCell>{cls.id}</TableCell>
                  <TableCell>{cls.groupName}</TableCell>
                  <TableCell>{cls.capacity ?? "—"}</TableCell>
                  <TableCell>
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                      <Select
                        value={cls.leaderAdultId ?? ""}
                        onChange={(e) =>
                          handleInlineTeacherChange(cls, e.target.value || null)
                        }
                        displayEmpty
                        renderValue={(value) => {
                          if (!value) return "— No teacher —";
                          const match = teachers.find((t) => t.adultId === value);
                          if (match) return `${match.adultName} ${match.adultSurname}`.trim();
                          return cls.leaderName || `Adult ${value}`;
                        }}
                      >
                        <MenuItem value="">
                          <em>— No teacher —</em>
                        </MenuItem>
                        {teachers.map((t) => (
                          <MenuItem key={t.adultId} value={t.adultId}>
                            {`${t.adultName} ${t.adultSurname}`.trim()}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 280 }}>{cls.notes || "—"}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenEdit(cls)}
                      title="Edit class"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeschedule(cls)}
                      title="Remove from this week"
                    >
                      <RemoveCircleOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {sortedClasses.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No classes for this week yet
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
        Teacher dropdowns only show staff scheduled for {selectedCampusName} this week.
        {onOpenStaffSchedule && (
          <>
            {" "}
            <Box
              component="span"
              onClick={onOpenStaffSchedule}
              sx={{
                color: "primary.main",
                cursor: "pointer",
                textDecoration: "underline",
              }}
            >
              Manage staff scheduling →
            </Box>
          </>
        )}
      </Typography>

      <CreateClassDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreate={handleCreate}
        campusName={selectedCampusName}
        weekLabel={weekLabel}
        teachers={teachers}
        teachersLoading={teachersLoading}
        saving={saving}
      />

      <EditClassDialog
        open={editOpen}
        onClose={() => { setEditOpen(false); setEditingClass(null); }}
        onSave={handleEditSave}
        classRow={editingClass}
        campusName={selectedCampusName}
        weekLabel={weekLabel}
        teachers={teachers}
        teachersLoading={teachersLoading}
        saving={saving}
      />

      <AddExistingClassDialog
        open={addExistingOpen}
        onClose={() => setAddExistingOpen(false)}
        onAdd={handleScheduleExisting}
        campusName={selectedCampusName}
        weekLabel={weekLabel}
        calendarWeekId={selectedWeek?.id}
        scheduling={scheduling}
      />

      <CopyFromWeekDialog
        open={copyFromWeekOpen}
        onClose={() => setCopyFromWeekOpen(false)}
        campusName={selectedCampusName}
        currentWeek={selectedWeek}
        allWeeks={allWeeks}
        formatWeekLabel={formatWeekLabel}
        showToast={showToast}
        onCopied={() => {
          loadClasses();
          onClassesChanged?.();
        }}
      />
    </Box>
  );
}

export default ManageClassesView;
